/**
 * Authentication utility functions for managing JWT tokens and user info.
 */

import { API_URL } from '../config';

const TOKEN_KEY        = 'ctta_admin_token';
const USER_KEY         = 'ctta_admin_user';
const BRANCH_THEME_KEY = 'ctta_branch_theme';
const USER_THEME_KEY   = 'ctta_user_theme';
const API_BASE_URL = API_URL;

// ------------------------------------------------------------------
// Token management
// ------------------------------------------------------------------

export const saveToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const getToken  = ()        => localStorage.getItem(TOKEN_KEY);
export const removeToken = ()      => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(BRANCH_THEME_KEY);
  localStorage.removeItem(USER_THEME_KEY);
};

// ------------------------------------------------------------------
// User info (role, branch_id, permissions)
// ------------------------------------------------------------------

export const saveUserInfo = (info) => localStorage.setItem(USER_KEY, JSON.stringify(info));
export const getUserInfo  = ()      => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)) || {}; } catch { return {}; }
};

// Theme helpers
export const getBranchTheme = () => localStorage.getItem(BRANCH_THEME_KEY) || 'purple';
export const getUserTheme   = () => localStorage.getItem(USER_THEME_KEY)   || '';
export const saveThemes = (branchTheme, userTheme) => {
  localStorage.setItem(BRANCH_THEME_KEY, branchTheme || 'purple');
  localStorage.setItem(USER_THEME_KEY,   userTheme   || '');
};

// Role helpers
export const getRole        = ()      => getUserInfo().role || '';
export const getBranchId    = ()      => getUserInfo().branch_id ?? null;
export const getPermissions = ()      => getUserInfo().permissions || {};
export const getCurrentUserId = ()   => getUserInfo().user_id ?? null;

export const isSuperAdmin   = ()      => getRole() === 'super_admin';
export const isBranchAdmin  = ()      => getRole() === 'branch_admin';
export const isUserManager  = ()      => ['super_admin', 'branch_admin'].includes(getRole());
export const hasPermission  = (perm)  => {
  if (isSuperAdmin()) return true;
  return getPermissions()[perm] === true;
};

// ------------------------------------------------------------------
// Authenticated fetch
// ------------------------------------------------------------------

export const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  // FormData (file uploads) must let the browser set its own multipart
  // boundary in Content-Type -- forcing application/json here would break it.
  const headers = options.body instanceof FormData
    ? { ...options.headers }
    : { ...options.headers, 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

// ------------------------------------------------------------------
// Token verification (round-trip to backend)
// ------------------------------------------------------------------

export const verifyToken = async () => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/verify`);
    if (response.ok) {
      const data = await response.json();
      if (data.valid) {
        // Refresh cached user info from server response
        saveUserInfo({
          user_id:      data.user_id,
          username:     data.username,
          display_name: data.display_name || '',
          role:         data.role,
          branch_id:    data.branch_id,
          permissions:  data.permissions,
        });
        saveThemes(data.branch_theme, data.user_theme);
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};
